import React, { useState, useMemo } from "react";
import {
  Box, Typography, TextField, Button, Divider, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
  Stack, Paper,
} from "@mui/material";
import {
  VideoCall as VideoIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  Link as LinkIcon,
  Notes as NotesIcon,
  Title as TitleIcon,
  Timer as TimerIcon,
  NotificationsActive as NotifyIcon,
} from "@mui/icons-material";
import axios from "axios";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

const API = "https://backend-global-1vq9.onrender.com";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px", fontSize: 14,
    "& fieldset": { borderColor: "#E8EDF2" },
    "&:hover fieldset": { borderColor: "#1976d2" },
    "&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#1976d2" },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px", fontSize: 14,
    "& fieldset": { borderColor: "#E8EDF2" },
    "&:hover fieldset": { borderColor: "#1976d2" },
    "&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: 2 },
  },
};

function FieldRow({ icon, children }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ mt: 1.75, color: "#1976d2", flexShrink: 0 }}>{icon}</Box>
      <Box flex={1}>{children}</Box>
    </Stack>
  );
}

const PLATFORMS = ["Zoom", "Google Meet", "Microsoft Teams", "Physical Meeting"];

const initialForm = {
  title: "",
  date: "",
  startTime: "",
  endTime: "",
  duration: "",
  platform: "Zoom",
  meetingLink: "",
  purpose: "",
  notifyMentor: true,
};

// ── Today's date as "YYYY-MM-DD" for the <input type="date" min> attr ──
const todayLocalISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const MentorSessions = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const mentorId = user?.id || user?._id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [sessionData, setSessionData] = useState(initialForm);

  // Memo so it doesn't change on every keystroke
  const minDate = useMemo(() => todayLocalISO(), []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSessionData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  // ── Future-date enforcement ────────────────────────────────────────────
  // Rules:
  //  • Date cannot be in the past.
  //  • If date is today, startTime must still be in the future.
  //  • endTime must be after startTime.
  const validateFutureSession = ({ date, startTime, endTime }) => {
    if (!date) return "Please pick a session date.";

    // Compare as Date objects so we don't rely on string ordering alone
    const today = new Date(todayLocalISO() + "T00:00:00");
    const chosenDay = new Date(date + "T00:00:00");
    if (isNaN(chosenDay.getTime())) return "Invalid date.";
    if (chosenDay < today) return "Session date must be today or later.";

    if (startTime && endTime && endTime <= startTime) {
      return "End time must be after start time.";
    }

    // If it's today, the start time must not already be in the past
    if (date === todayLocalISO() && startTime) {
      const startDateTime = new Date(`${date}T${startTime}`);
      if (startDateTime.getTime() <= Date.now()) {
        return "For today's date, start time must be in the future.";
      }
    }

    return null;
  };

  const handleCreateSession = async () => {
    const { title, date, startTime, endTime, duration, purpose } = sessionData;

    if (!title || !date || !startTime || !endTime || !duration || !purpose) {
      setError("Please fill in all required fields.");
      return;
    }
    if (!mentorId) {
      setError("You must be signed in as a mentor to create a session.");
      return;
    }

    const futureCheck = validateFutureSession({ date, startTime, endTime });
    if (futureCheck) {
      setError(futureCheck);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await axios.post(
        `${API}/api/sessions`,
        { ...sessionData, mentorId },
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.data) {
        setSuccess("Session created successfully!");
        setSessionData(initialForm);
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "Failed to create session. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#F3F6FA" }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 6, lg: 20 }, py: { xs: 3, md: 5 } }}>

        <Box textAlign="center" mb={{ xs: 3, md: 4 }}>
          <Typography fontWeight={800} color="#0D1117"
            sx={{ fontSize: { xs: 22, sm: 28, md: 32 }, lineHeight: 1.2 }}>
            Schedule Mentorship Session
          </Typography>
          <Typography fontSize={13} color="text.secondary" mt={0.75}>
            Set up a new session for your mentees
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError("")}
            sx={{ mb: 2.5, borderRadius: "12px", maxWidth: 740, mx: "auto", fontSize: 13 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess("")}
            sx={{ mb: 2.5, borderRadius: "12px", maxWidth: 740, mx: "auto", fontSize: 13 }}>
            {success}
          </Alert>
        )}

        <Paper elevation={0} sx={{
          maxWidth: 740, mx: "auto",
          borderRadius: "18px", border: "1px solid #E8EDF2",
          p: { xs: 2.5, sm: 4 },
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
            <Box sx={{ width: 4, height: 28, borderRadius: "2px", bgcolor: "#1976d2" }} />
            <Typography fontWeight={700} fontSize={16} color="#0D1117">
              Create New Session
            </Typography>
          </Stack>
          <Divider sx={{ mb: 3 }} />

          <Stack spacing={2.5}>

            <FieldRow icon={<TitleIcon sx={{ fontSize: 20 }} />}>
              <TextField label="Session Title *" name="title" value={sessionData.title}
                onChange={handleChange} fullWidth size="small" sx={fieldSx} />
            </FieldRow>

            {/* Date — min={today} blocks the calendar from offering past dates */}
            <FieldRow icon={<CalendarIcon sx={{ fontSize: 20 }} />}>
              <TextField
                label="Session Date *"
                name="date"
                type="date"
                value={sessionData.date}
                onChange={handleChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minDate }}
                helperText="Must be today or later"
                sx={fieldSx}
              />
            </FieldRow>

            <FieldRow icon={<TimeIcon sx={{ fontSize: 20 }} />}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField label="Start Time *" name="startTime" type="time" value={sessionData.startTime}
                  onChange={handleChange} fullWidth size="small" InputLabelProps={{ shrink: true }} sx={fieldSx} />
                <TextField label="End Time *" name="endTime" type="time" value={sessionData.endTime}
                  onChange={handleChange} fullWidth size="small" InputLabelProps={{ shrink: true }} sx={fieldSx} />
              </Stack>
            </FieldRow>

            <FieldRow icon={<TimerIcon sx={{ fontSize: 20 }} />}>
              <TextField label="Duration *" name="duration" value={sessionData.duration}
                onChange={handleChange} placeholder="e.g. 60 minutes" fullWidth size="small" sx={fieldSx} />
            </FieldRow>

            <FieldRow icon={<VideoIcon sx={{ fontSize: 20 }} />}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>Platform</InputLabel>
                <Select name="platform" value={sessionData.platform} onChange={handleChange} label="Platform">
                  {PLATFORMS.map((p) => (
                    <MenuItem key={p} value={p} sx={{ fontSize: 14 }}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FieldRow>

            <FieldRow icon={<LinkIcon sx={{ fontSize: 20 }} />}>
              <TextField label="Meeting Link" name="meetingLink" value={sessionData.meetingLink}
                onChange={handleChange} placeholder="https://..." fullWidth size="small" sx={fieldSx} />
            </FieldRow>

            <FieldRow icon={<NotesIcon sx={{ fontSize: 20 }} />}>
              <TextField label="Meeting Purpose *" name="purpose" value={sessionData.purpose}
                onChange={handleChange} multiline rows={3} fullWidth size="small" sx={fieldSx} />
            </FieldRow>

            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pl: 0.5 }}>
              <NotifyIcon sx={{ fontSize: 20, color: "#1976d2", flexShrink: 0 }} />
              <FormControlLabel
                control={
                  <Switch checked={sessionData.notifyMentor} onChange={handleChange} name="notifyMentor"
                    size="small" />
                }
                label={<Typography fontSize={14} fontWeight={500} color="#3D4B5C">Notify Mentor</Typography>}
                sx={{ m: 0 }}
              />
            </Stack>

            <Typography fontSize={12} color="#8A9BB0" sx={{ pl: 4.5 }}>
              * Required fields
            </Typography>

            <Button variant="contained" onClick={handleCreateSession} disabled={loading}
              sx={{
                bgcolor: "#1976d2", "&:hover": { bgcolor: "#1565C0" },
                borderRadius: "12px", textTransform: "none",
                fontWeight: 700, fontSize: 14, py: 1.5, boxShadow: "none",
                "&:hover:not(:disabled)": { boxShadow: "0 6px 20px rgba(25,118,210,0.30)" },
                "&.Mui-disabled": { opacity: 0.6 },
              }}>
              {loading
                ? <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} color="inherit" />
                    <span>Creating Session…</span>
                  </Stack>
                : "Create Session"}
            </Button>
          </Stack>
        </Paper>
      </Box>

      <Footer />
    </Box>
  );
};

export default MentorSessions;